import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { fetchWrapper } from '@/lib/fetch-wrapper';

export interface BatchRunRow {
  id: number;
  trigger: string;
  status: string;
  scope: string | null;
  started_at: string;
  finished_at: string | null;
  error: string | null;
  module?: string | null;
}

interface BatchRunTableProps<TChild> {
  listUrl: string;
  detailUrl: (id: number) => string;
  title?: string;
  testId?: string;
  triggerFilter?: 'manual' | 'cron';
  statusColors?: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'>;
  extraColumns?: (row: BatchRunRow) => React.ReactNode;
  renderChildHeader: () => React.ReactNode;
  renderChildRow: (child: TChild) => React.ReactNode;
  getChildren: (detail: unknown) => TChild[];
}

function statusChip(
  status: string,
  colors: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'>,
  error?: string | null,
) {
  return (
    <Chip
      size="small"
      label={status}
      color={colors[status] ?? 'default'}
      title={error ?? undefined}
    />
  );
}

function BatchRunDetailRow<TChild>({
  row,
  detailUrl,
  statusColors,
  extraColumns,
  renderChildHeader,
  renderChildRow,
  getChildren,
}: {
  row: BatchRunRow;
  detailUrl: (id: number) => string;
  statusColors: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'>;
  extraColumns?: (row: BatchRunRow) => React.ReactNode;
  renderChildHeader: () => React.ReactNode;
  renderChildRow: (child: TChild) => React.ReactNode;
  getChildren: (detail: unknown) => TChild[];
}) {
  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState<TChild[] | null>(null);
  const [loading, setLoading] = useState(false);

  const loadChildren = async () => {
    if (children !== null) {
      setOpen((v) => !v);
      return;
    }
    setLoading(true);
    try {
      const detail = await fetchWrapper.get(detailUrl(row.id));
      setChildren(getChildren(detail));
      setOpen(true);
    } catch {
      setChildren([]);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const colSpan = extraColumns ? 8 : 7;

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            onClick={() => loadChildren()}
            disabled={loading}
            aria-label="expand row">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.id}</TableCell>
        <TableCell>{row.trigger}</TableCell>
        {extraColumns?.(row)}
        <TableCell>{statusChip(row.status, statusColors, row.error)}</TableCell>
        <TableCell>{row.scope ?? '—'}</TableCell>
        <TableCell>{new Date(row.started_at).toLocaleString()}</TableCell>
        <TableCell>
          {row.finished_at
            ? new Date(row.finished_at).toLocaleString()
            : '—'}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: open ? undefined : 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1 }}>
              {children === null || loading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading…
                </Typography>
              ) : children.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No child runs.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>{renderChildHeader()}</TableHead>
                  <TableBody>
                    {children.map((child, idx) => (
                      <React.Fragment key={idx}>
                        {renderChildRow(child)}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function BatchRunTable<TChild>({
  listUrl,
  detailUrl,
  title = 'Batch run history',
  testId,
  triggerFilter,
  statusColors = {},
  extraColumns,
  renderChildHeader,
  renderChildRow,
  getChildren,
}: BatchRunTableProps<TChild>) {
  const [rows, setRows] = useState<BatchRunRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWrapper.get(listUrl);
      const all = Array.isArray(data) ? data : [];
      setRows(
        triggerFilter
          ? all.filter((row: BatchRunRow) => row.trigger === triggerFilter)
          : all,
      );
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [listUrl, triggerFilter]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}>
        <Typography variant="subtitle1">{title}</Typography>
        <Button size="small" onClick={() => load()} data-testid={`${testId}-refresh`}>
          Refresh
        </Button>
      </Box>
      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          {triggerFilter === 'manual'
            ? 'No manual runs yet.'
            : triggerFilter === 'cron'
              ? 'No scheduled runs yet.'
              : 'No batch runs yet.'}
        </Alert>
      ) : (
        <Table size="small" data-testid={testId}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Id</TableCell>
              <TableCell>Trigger</TableCell>
              {extraColumns && <TableCell>Module</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Finished</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <BatchRunDetailRow
                key={row.id}
                row={row}
                detailUrl={detailUrl}
                statusColors={statusColors}
                extraColumns={extraColumns}
                renderChildHeader={renderChildHeader}
                renderChildRow={renderChildRow}
                getChildren={getChildren}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

export default BatchRunTable;
